import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Button, Select, MenuItem, TextField, Box, Tooltip, Typography, Dialog, DialogTitle, DialogContent, DialogActions, FormGroup, FormControlLabel, Checkbox, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import SaveIcon from '@mui/icons-material/Save';
import SettingsIcon from '@mui/icons-material/Settings';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NotesIcon from '@mui/icons-material/Notes';
import { useI18n } from '../i18n';

const STATUS_BASE = [
  { value: 'new', label: 'Новый', color: '#43a047' },
  { value: 'processing', label: 'В обработке', color: '#1976d2' },
  { value: 'completed', label: 'Готово', color: '#388e3c' },
  { value: 'clarification', label: 'Уточнения', color: '#fbc02d' },
  { value: 'refund', label: 'Возврат', color: '#8e24aa' },
  { value: 'pending', label: 'Ожидание', color: '#757575' },
  { value: 'cancelled', label: 'Отменено', color: '#d32f2f' },
  { value: 'failed', label: 'Ошибка', color: '#d32f2f' },
];

const EDIT_BLOCKED_STATUSES = ['completed', 'clarification', 'failed', 'cancelled'];
const EMPTY_PENDING_EDIT_VALUES = { email: '', phone_dist: '', amount_dist: '', service_name: '' };
const TABLE_FONT_FAMILY = '"Google Sans", "Segoe UI", Arial, sans-serif';
const GOOGLE_SANS_STYLESHEET_ID = 'admin-google-sans-font';
const GOOGLE_SANS_PRECONNECT_ID = 'admin-google-sans-preconnect';
const GOOGLE_SANS_GSTATIC_PRECONNECT_ID = 'admin-google-sans-gstatic-preconnect';
const GOOGLE_SANS_STYLESHEET_HREF = 'https://fonts.googleapis.com/css2?family=Google+Sans:wght@400;500;600;700&display=swap';

function ensureHeadLink({ id, rel, href, crossOrigin }) {
  if (typeof document === 'undefined') {
    return;
  }

  if (document.getElementById(id)) {
    return;
  }

  const link = document.createElement('link');
  link.id = id;
  link.rel = rel;
  link.href = href;
  if (crossOrigin) {
    link.crossOrigin = crossOrigin;
  }
  document.head.appendChild(link);
}

function getStatusText(status, options) {
  const opt = options.find(s => s.value === status);
  return <span style={{ color: opt?.color || '#222', fontWeight: 600 }}>{opt?.label || status}</span>;
}

function CopyableCell({ value }) {
  const { t } = useI18n();
  const [copied, setCopied] = React.useState(false);
  return (
    <Tooltip title={copied ? t('Скопировано!') : t('Кликните для копирования')} arrow>
      <span
        style={{ cursor: 'pointer', color: copied ? '#388e3c' : '#222', fontWeight: 600, userSelect: 'none' }}
        onClick={async (e) => {
          e.stopPropagation();
          try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1000);
          } catch {}
        }}
      >
        {value}
      </span>
    </Tooltip>
  );
}

// Компонент для отображения ключ-значение
function InfoTable({ data }) {
  if (!data) return null;
  return (
    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', mt: 1 }}>
      <tbody>
        {Object.entries(data).map(([key, value]) => (
          <tr key={key}>
            <td style={{ fontWeight: 600, padding: '4px 12px 4px 0', verticalAlign: 'top', color: '#1976d2', whiteSpace: 'nowrap' }}>{key}</td>
            <td style={{ padding: '4px 0', wordBreak: 'break-all', color: '#222' }}>{typeof value === 'object' && value !== null ? JSON.stringify(value, null, 2) : String(value)}</td>
          </tr>
        ))}
      </tbody>
    </Box>
  );
}

const ALL_COLUMNS = [
  { field: 'source', headerName: 'От', width: 10, renderCell: (params) => {
      const val = params.row?.source;
      if (!val) return '-';
      const full = String(val);
      const abbrev = full.charAt(0).toUpperCase();
      return (
        <Tooltip title={full} arrow>
          <span style={{ fontWeight: 700 }}>{abbrev}</span>
        </Tooltip>
      );
    }
  },
  { field: 'payment_time', headerName: 'Дата и время оплаты', width: 160, renderCell: (params) => {
      const val = params.row?.payment_time;
      if (!val) return '-';
      const d = new Date(val.replace(' ', 'T'));
      if (isNaN(d.getTime())) return val;
      return d.toLocaleString(params.dateLocale);
    }
  },
  { field: 'external_order_id', headerName: 'Номер заказа', width: 170,renderCell: (params) => {
    return <CopyableCell value={params.row?.external_order_id || ''} />;
  } 
  },
  { field: 'phone_src', headerName: 'Клиент', width: 130 },
  { field: 'amount_src', headerName: 'Сумма', width: 90, renderCell: (params) => {
    const val = params.row?.amount_src;
    if (val == null) return '-';
    return val.toString();
  } },
  { field: 'currency_src', headerName: 'Валюта', width: 70 },
  { field: 'lang', headerName: 'Язык', width: 60, renderCell: (params) => String(params.row?.lang).charAt(0).toUpperCase() + String(params.row?.lang).slice(1) || '-' },
  { field: 'service_name', headerName: 'Сервис', width: 110 },
  { field: 'phone_dist', headerName: 'Номер пополнения', width: 110, renderCell: (params) => {
      return <CopyableCell value={params.row?.phone_dist || ''} />;
    }
  },
  { field: 'amount_dist', headerName: 'Пополняемая сумма', width: 110, renderCell: (params) => {
    const val = params.row?.amount_dist;
    if (val == null) return '-';
    return val.toString().split(/[.,]/)[0];
  } },
  {
    field: 'order_status',
    headerName: 'Статус',
    width: 150,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => {
      const row = params.row;
      const isMine = row.assigned_operator === params.operator;
      const isAdmin = params.adminRole === 'admin';
      const currentStatus = params.statusOptions.find(opt => opt.value === row.order_status);
      // Для операторов блокируем только completed, failed, cancelled
      const operatorBlocked = ['completed', 'failed', 'cancelled'];
      const isEditBlocked = isAdmin ? false : operatorBlocked.includes(row.order_status);
      const canEditStatus = isAdmin || (isMine && !isEditBlocked);
      return canEditStatus ? (
        <Select
          size="small"
          value={row.order_status}
          onChange={e => {
            if (e.target.value === 'clarification') {
              params.handleOpenClarification(row.id, params.noteValues[row.id]?.clarification_note || '');
            } else if (e.target.value === 'refund') {
              params.handleOpenRefund(row.id, params.noteValues[row.id]?.refund_note || '');
            } else {
              params.onStatusChange(row.id, e.target.value);
            }
          }}
          variant="standard"
          disableUnderline
          sx={{
            minWidth: 90,
            textAlign: 'center',
            fontWeight: 600,
            color: currentStatus?.color || '#222',
            background: 'none',
            border: 0,
            p: 0,
            m: 0,
            '& .MuiSelect-select': {
              color: currentStatus?.color || '#222',
              fontWeight: 600,
              textAlign: 'center',
            },
            '& .MuiSelect-icon': {
              color: currentStatus?.color || '#222',
            },
          }}
          MenuProps={{ PaperProps: { sx: { textAlign: 'center' } } }}
        >
          {params.statusOptions.map(opt => (
            <MenuItem key={opt.value} value={opt.value} sx={{ textAlign: 'center', color: opt.color, fontWeight: 600 }}>{opt.label}</MenuItem>
          ))}
        </Select>
      ) : getStatusText(row.order_status, params.statusOptions);
    }
  },
  {
    field: 'assigned_operator',
    headerName: 'Оператор',
    width: 120,
    renderCell: (params) => {
      const row = params.row;
      const isMine = row.assigned_operator === params.operator;
      return (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {row.assigned_operator ? (
            <Typography variant="body2" sx={{ color: '#222', fontWeight: isMine ? 700 : 400, textAlign: 'center', fontSize: 15, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{row.assigned_operator}</Typography>
          ) : <span style={{ color: '#bbb', textAlign: 'center', fontWeight: 600, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>-</span>}
        </Box>
      );
    }
  },
  {
    field: 'actions',
    headerName: 'Действие',
    width: 220,
    disableColumnMenu: true,
    sortable: false,
    filterable: false,
    renderCell: (params) => {
      const row = params.row;
      const pendingIsAvailable = row.order_status === 'pending'
        && (!row.assigned_operator || row.assigned_operator === 'System');
      const pendingCanEdit = row.order_status === 'pending'
        && (
          !row.assigned_operator
          || row.assigned_operator === 'System'
          || row.assigned_operator === params.operator
          || params.adminRole === 'admin'
        );
      if (params.adminRole === 'controller') {
        return null;
      }
      const canTake = row.order_status === 'new'
        || (row.order_status === 'clarification' && row.assigned_operator !== params.operator)
        || pendingIsAvailable;
      const canEditPending = pendingCanEdit
        && params.adminRole !== 'controller';

      if (!canTake && !canEditPending) {
        return null;
      }
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, width: '100%', height: '100%' }}>
          {canTake && (
            <Button variant="contained" size="small" startIcon={<PlayArrowIcon />} sx={{ bgcolor: '#222', color: '#fff', '&:hover': { bgcolor: '#111' }, borderRadius: 2, fontWeight: 600, whiteSpace: 'nowrap' }} onClick={() => params.onTake(row.id)}>{params.t('Взять')}</Button>
          )}
          {canEditPending && (
            <Tooltip title={params.t('Изменить')} arrow>
              <IconButton
                size="small"
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
                onClick={(e) => {
                  e.stopPropagation();
                  params.openPendingEditDialog(row);
                }}
              >
                <AutorenewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      );
    }
  },
  {
    field: 'internal_note',
    headerName:  <EditIcon fontSize="small" />,
    disableColumnMenu: true,
    width: 90,
    renderCell: (params) => {
      const row = params.row;
      const isMine = row.assigned_operator === params.operator;
      const isEditBlocked = EDIT_BLOCKED_STATUSES.includes(row.order_status);
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 90, width: '100%', height: '100%' }}>
          {isMine && !isEditBlocked && (
            <IconButton
              size="small"
              sx={{
                p: 0,
                m: 0,
                borderRadius: 0,
                background: 'none',
                '&:hover': {
                  background: 'none',
                  boxShadow: 'none',
                },
                color: '#1976d2',
              }}
              onClick={() => params.setNoteDialog({ open: true, orderId: row.id, value: row.internal_note || '' })}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
          <Tooltip
            title={<span style={{ fontSize: 20, maxWidth: 400, display: 'block', whiteSpace: 'pre-line' }}>{row.internal_note || '-'}</span>}
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  fontSize: 20,
                  maxWidth: 400,
                  padding: '12px 18px',
                  whiteSpace: 'pre-line',
                }
              }
            }}
          >
            {row.internal_note ? (
              <NotesIcon fontSize="small" sx={{ color: '#1976d2', display: 'block', margin: '0 auto' }} />
            ) : (
              <span style={{ color: '#bbb', fontSize: 18, fontWeight: 400, display: 'block', margin: '0 auto' }}> </span>
            )}
          </Tooltip>
        </Box>
      );
    }
  },
  {
    field: 'info',
    headerName:  <InfoOutlinedIcon fontSize="small" sx={{ color: 'black' }} />,
    width: 50,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => {
      return (
        <Tooltip title={params.t('Информация о заказе')} arrow>
          <IconButton size="small" onClick={e => { e.stopPropagation(); params.openInfoDialog(params.row); }}>
            <InfoOutlinedIcon fontSize="small" sx={{ color: '#1976d2' }} />
          </IconButton>
        </Tooltip>
      );
    }
  },
];

const COLUMN_LABEL_KEYS = {
  source: 'От',
  payment_time: 'Дата и время оплаты',
  external_order_id: 'Номер заказа',
  phone_src: 'Клиент',
  amount_src: 'Сумма',
  currency_src: 'Валюта',
  lang: 'Язык',
  service_name: 'Сервис',
  phone_dist: 'Номер пополнения',
  amount_dist: 'Пополняемая сумма',
  order_status: 'Статус',
  assigned_operator: 'Оператор',
  actions: 'Действие',
  internal_note: 'Примечание',
};

const buildLocaleText = (t) => ({
  toolbarDensity: t('Плотность'),
  toolbarDensityLabel: t('Плотность'),
  toolbarDensityCompact: t('Компактно'),
  toolbarDensityStandard: t('Стандартно'),
  toolbarDensityComfortable: t('Комфортно'),
  toolbarColumns: t('Столбцы'),
  toolbarColumnsLabel: t('Выбрать столбцы'),
  toolbarFilters: t('Фильтры'),
  toolbarFiltersLabel: t('Показать фильтры'),
  toolbarFiltersTooltipHide: t('Скрыть фильтры'),
  toolbarFiltersTooltipShow: t('Показать фильтры'),
  toolbarExport: t('Экспорт'),
  toolbarExportLabel: t('Экспорт'),
  toolbarExportCSV: t('Скачать CSV'),
  toolbarExportPrint: t('Печать'),
  columnsPanelTextFieldLabel: t('Поиск столбца'),
  columnsPanelTextFieldPlaceholder: t('Название столбца'),
  columnsPanelDragIconLabel: t('Изменить порядок столбцов'),
  columnsPanelShowAllButton: t('Показать все'),
  columnsPanelHideAllButton: t('Скрыть все'),
  columnMenuSortAsc: t('Сортировать по возрастанию'),
  columnMenuSortDesc: t('Сортировать по убыванию'),
  columnMenuUnsort: t('Сбросить сортировку'),
  columnMenuFilter: t('Фильтр'),
  columnMenuHideColumn: t('Скрыть'),
  columnMenuShowColumns: t('Показать столбцы'),
  noRowsLabel: t('Нет данных'),
  noResultsOverlayLabel: t('Нет результатов'),
  footerRowSelected: (count) => {
    if (count === 1) return t('1 строка выбрана');
    if (count >= 2 && count <= 4) return t('{count} строки выбраны').replace('{count}', count);
    return t('{count} строк выбрано').replace('{count}', count);
  },
  footerTotalRows: t('Всего строк:'),
  MuiTablePagination: {},
  sortAscending: t('Сортировать по возрастанию'),
  sortDescending: t('Сортировать по убыванию'),
  filterPanelAddFilter: t('Добавить фильтр'),
  filterPanelDeleteIconLabel: t('Удалить'),
  filterPanelOperators: t('Операторы'),
  filterPanelOperatorAnd: t('И'),
  filterPanelOperatorOr: t('ИЛИ'),
  filterPanelColumns: t('Столбцы'),
  filterPanelInputLabel: t('Значение'),
  filterPanelInputPlaceholder: t('Значение фильтра'),
  checkboxSelectionHeaderName: t('Выбрать все'),
  footerPaginationRowsPerPage: t('Строк на странице:'),
  columnMenuLabel: t('Меню'),
});

function NoteDialogInput({ value, onChange }) {
  const { t } = useI18n();
  return <TextField
    autoFocus
    margin="dense"
    label={t('Примечание')}
    type="text"
    fullWidth
    value={value}
    onChange={e => onChange(e.target.value)}
  />;
}

const PendingEditDialog = React.memo(function PendingEditDialog({
  open,
  orderId,
  initialValues,
  editableServices,
  onClose,
  onSubmit,
}) {
  const { t } = useI18n();
  const [values, setValues] = useState(initialValues);
  const [savingMode, setSavingMode] = useState(null);

  useEffect(() => {
    if (!open) return;
    setValues(initialValues);
    setSavingMode(null);
  }, [open, initialValues]);

  const pendingEditServiceOptions = useMemo(() => {
    const options = Array.isArray(editableServices) ? [...editableServices] : [];
    const currentValue = values.service_name;
    if (currentValue && !options.includes(currentValue)) {
      options.unshift(currentValue);
    }
    return options;
  }, [editableServices, values.service_name]);

  const pendingEditAmount = Number(values.amount_dist);
  const pendingEditEmail = String(values.email || '').trim();
  const isPendingEditValid = values.phone_dist.trim() !== ''
    && values.service_name.trim() !== ''
    && Number.isFinite(pendingEditAmount)
    && pendingEditAmount > 0
    && (pendingEditEmail === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(pendingEditEmail));

  const handleFieldChange = useCallback((field, value) => {
    setValues(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleDialogClose = useCallback(() => {
    if (savingMode) return;
    onClose();
  }, [onClose, savingMode]);

  const handleSubmit = useCallback(async (requeue) => {
    if (!orderId || !onSubmit || !isPendingEditValid) {
      return;
    }

    setSavingMode(requeue ? 'saveAndRetry' : 'save');
    try {
      await onSubmit(orderId, values, { requeue });
      onClose();
    } catch (e) {
      // Ошибку показывает родитель через snackbar, а форму оставляем открытой.
    } finally {
      setSavingMode(null);
    }
  }, [isPendingEditValid, onClose, onSubmit, orderId, values]);

  return (
    <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('Изменить заказ')}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label={t('Email')}
          type="email"
          fullWidth
          value={values.email}
          onChange={e => handleFieldChange('email', e.target.value)}
        />
        <TextField
          margin="dense"
          label={t('Номер пополнения')}
          type="text"
          fullWidth
          value={values.phone_dist}
          onChange={e => handleFieldChange('phone_dist', e.target.value)}
        />
        <TextField
          margin="dense"
          label={t('Пополняемая сумма')}
          type="number"
          fullWidth
          value={values.amount_dist}
          onChange={e => handleFieldChange('amount_dist', e.target.value)}
          inputProps={{ min: 0, step: '0.01' }}
        />
        <TextField
          margin="dense"
          label={t('Сервис')}
          select
          fullWidth
          value={values.service_name}
          onChange={e => handleFieldChange('service_name', e.target.value)}
        >
          {pendingEditServiceOptions.map(service => (
            <MenuItem key={service} value={service}>{service}</MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDialogClose} disabled={Boolean(savingMode)}>{t('Отмена')}</Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={() => handleSubmit(false)}
          disabled={!isPendingEditValid || Boolean(savingMode)}
          sx={{ bgcolor: '#111', color: '#fff', '&:hover': { bgcolor: '#222' } }}
        >
          {savingMode === 'save' ? t('Сохранение...') : t('Сохранить')}
        </Button>
        <Button
          variant="contained"
          startIcon={<AutorenewIcon />}
          onClick={() => handleSubmit(true)}
          disabled={!isPendingEditValid || Boolean(savingMode)}
          sx={{ bgcolor: '#111', color: '#fff', '&:hover': { bgcolor: '#222' } }}
        >
          {savingMode === 'saveAndRetry' ? t('Сохранение...') : t('Сохранить и повторить')}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export function OrderTable({ orders, onTake, onStatusChange, onRefund, refundingOrderIds = {}, onPendingOrderEditSave, operator, noteValues, onNoteChange, onInternalNoteChange, adminRole, editableServices = [] }) {
  const { t, lang } = useI18n();
  const dateLocale = lang === 'ru' ? 'ru-RU' : lang === 'tr' ? 'tr-TR' : 'en-US';

  useEffect(() => {
    ensureHeadLink({
      id: GOOGLE_SANS_PRECONNECT_ID,
      rel: 'preconnect',
      href: 'https://fonts.googleapis.com',
    });
    ensureHeadLink({
      id: GOOGLE_SANS_GSTATIC_PRECONNECT_ID,
      rel: 'preconnect',
      href: 'https://fonts.gstatic.com',
      crossOrigin: 'anonymous',
    });
    ensureHeadLink({
      id: GOOGLE_SANS_STYLESHEET_ID,
      rel: 'stylesheet',
      href: GOOGLE_SANS_STYLESHEET_HREF,
    });
  }, []);

  const statusOptions = useMemo(
    () => STATUS_BASE.map(opt => ({ ...opt, label: t(opt.label) })),
    [t]
  );
  const columnLabels = useMemo(
    () => Object.fromEntries(Object.entries(COLUMN_LABEL_KEYS).map(([key, label]) => [key, t(label)])),
    [t]
  );
  const dataGridLocale = useMemo(() => buildLocaleText(t), [t]);
  const [clarificationDialog, setClarificationDialog] = useState({ open: false, orderId: null, value: '' });
  const [refundDialog, setRefundDialog] = useState({ open: false, orderId: null, value: '' });
  const [viewDialog, setViewDialog] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(ALL_COLUMNS.map(col => col.field));
  const [noteDialog, setNoteDialog] = useState({ open: false, orderId: null, value: '' });
  const [search, setSearch] = useState('');
  const [infoDialog, setInfoDialog] = useState({ open: false, row: null });
  const [pendingEditDialog, setPendingEditDialog] = useState({
    open: false,
    orderId: null,
    initialValues: EMPTY_PENDING_EDIT_VALUES,
  });

  // Фильтрация по поиску
  const filteredOrders = React.useMemo(() => {
    if (!search) return orders;
    const term = search.toLowerCase();
    return orders.filter(order =>
      (order.external_order_id || '').toLowerCase().includes(term) ||
      (order.phone_src || '').toLowerCase().includes(term) ||
      (order.service_name || '').toLowerCase().includes(term) ||
      (order.phone_dist || '').toLowerCase().includes(term) ||
      (order.order_status || '').toLowerCase().includes(term) ||
      (order.assigned_operator || '').toLowerCase().includes(term)
    );
  }, [orders, search]);

  const handleOpenClarification = useCallback((orderId, currentValue = '') => {
    setClarificationDialog({ open: true, orderId, value: currentValue });
  }, []);
  const handleOpenRefund = useCallback((orderId, currentValue = '') => {
    setRefundDialog({ open: true, orderId, value: currentValue });
  }, []);
  const handleCloseClarification = useCallback(() => {
    setClarificationDialog({ open: false, orderId: null, value: '' });
  }, []);
  const refundInProgress = Boolean(refundingOrderIds[refundDialog.orderId]);
  const handleCloseRefund = useCallback(() => {
    if (refundingOrderIds[refundDialog.orderId]) return;
    setRefundDialog({ open: false, orderId: null, value: '' });
  }, [refundDialog.orderId, refundingOrderIds]);
  const handleOpenPendingEditDialog = useCallback((row) => {
    setPendingEditDialog({
      open: true,
      orderId: row.id,
      initialValues: {
        email: row.email || '',
        phone_dist: String(row.phone_dist || ''),
        amount_dist: row.amount_dist ?? '',
        service_name: String(row.service_name || editableServices[0] || ''),
      },
    });
  }, [editableServices]);
  const handleClosePendingEditDialog = useCallback(() => {
    setPendingEditDialog({
      open: false,
      orderId: null,
      initialValues: EMPTY_PENDING_EDIT_VALUES,
    });
  }, []);
  const handleSaveClarification = () => {
    if (clarificationDialog.orderId && clarificationDialog.value) {
      onNoteChange(clarificationDialog.orderId, clarificationDialog.value, 'clarification_note');
      onStatusChange(clarificationDialog.orderId, 'clarification');
    }
    handleCloseClarification();
  };
  const handleSaveRefund = async () => {
    if (!refundDialog.orderId || !refundDialog.value.trim() || refundInProgress) return;

    try {
      await onRefund(refundDialog.orderId, refundDialog.value);
      setRefundDialog({ open: false, orderId: null, value: '' });
    } catch {
      // The parent keeps the order unchanged and displays the provider error.
    }
  };
  const openInfoDialog = useCallback((row) => {
    setInfoDialog({ open: true, row });
  }, []);

  // Пробрасываем нужные пропсы в renderCell
  const columns = useMemo(() => ALL_COLUMNS.filter(col => visibleColumns.includes(col.field)).map(col => {
    const headerName = typeof col.headerName === 'string' ? t(col.headerName) : col.headerName;
    return {
      ...col,
      headerName,
      align: 'center',
      headerAlign: 'center',
      renderCell: col.renderCell
        ? (params) => col.renderCell({
            ...params,
            operator,
            onTake,
            onStatusChange,
            noteValues,
            onNoteChange,
            onInternalNoteChange,
            openPendingEditDialog: handleOpenPendingEditDialog,
            handleOpenClarification,
            handleOpenRefund,
            setNoteDialog,
            openInfoDialog,
            adminRole,
            t,
            statusOptions,
            lang,
            dateLocale,
          })
        : undefined,
    };
  }), [
    adminRole,
    dateLocale,
    handleOpenClarification,
    handleOpenPendingEditDialog,
    handleOpenRefund,
    lang,
    noteValues,
    onInternalNoteChange,
    onNoteChange,
    onStatusChange,
    onTake,
    openInfoDialog,
    operator,
    statusOptions,
    t,
    visibleColumns,
  ]);

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 });

  return (
    <Box
      sx={{
        flex: 1,
        width: '100%',
        bgcolor: '#fafbfc',
        p: 2,
        borderRadius: 2,
        boxShadow: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        fontFamily: TABLE_FONT_FAMILY,
        '& .MuiTypography-root, & .MuiButton-root, & .MuiInputBase-root, & .MuiFormControlLabel-label, & .MuiDialogTitle-root, & .MuiDialogContent-root, & .MuiDialogActions-root, & .MuiMenuItem-root': {
          fontFamily: TABLE_FONT_FAMILY,
        },
      }}
    >
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <TextField
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('Поиск...')}
          size="small"
          sx={{ minWidth: 220, bgcolor: '#fff', borderRadius: 2 }}
        />
        <Button
          variant="contained"
          startIcon={<SettingsIcon />}
          sx={{ bgcolor: '#222', color: '#fff', '&:hover': { bgcolor: '#111' }, borderRadius: 2, fontWeight: 600 }}
          onClick={() => setViewDialog(true)}
        >
          {t('Вид')}
        </Button>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <DataGrid
          rows={filteredOrders}
          columns={columns}
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50, 100]}
          disableRowSelectionOnClick
          getRowId={row => row.id}
          autoHeight={false}
          localeText={dataGridLocale}
          sx={{
            border: 0,
            flex: 1,
            fontFamily: TABLE_FONT_FAMILY,
            '& .MuiDataGrid-columnHeaders': {
              bgcolor: '#f5f5f5',
              fontWeight: 400,
              fontSize: '0.875rem',
              border: 0,
              fontFamily: TABLE_FONT_FAMILY,
            },
            '& .MuiDataGrid-cell': {
              fontSize: '0.875rem',
              fontWeight: 600,
              border: 0,
              textAlign: 'center',
              justifyContent: 'center',
              alignItems: 'center',
              fontFamily: TABLE_FONT_FAMILY,
              borderBottom: '1px solid #e0e0e0',
            },
            '& .MuiDataGrid-row': { bgcolor: '#fff', border: 0 },
            '& .MuiDataGrid-footerContainer': { border: 0 },
            '& .MuiDataGrid-virtualScroller': { minHeight: 0 },
          }}
        />
      </Box>
      <Dialog open={clarificationDialog.open} onClose={handleCloseClarification}>
        <DialogTitle>{t('Уточнение для клиента')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('Сообщение клиенту')}
            type="text"
            fullWidth
            value={clarificationDialog.value}
            onChange={e => setClarificationDialog(d => ({ ...d, value: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseClarification}>{t('Отмена')}</Button>
          <Button onClick={handleSaveClarification} disabled={!clarificationDialog.value.trim()} variant="contained">{t('Сохранить')}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={refundDialog.open} onClose={handleCloseRefund}>
        <DialogTitle>{t('Причина возврата')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('Причина возврата')}
            type="text"
            fullWidth
            value={refundDialog.value}
            disabled={refundInProgress}
            onChange={e => setRefundDialog(d => ({ ...d, value: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRefund} disabled={refundInProgress}>{t('Отмена')}</Button>
          <Button onClick={handleSaveRefund} disabled={!refundDialog.value.trim() || refundInProgress} variant="contained">
            {refundInProgress ? t('Возврат...') : t('Сохранить')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={viewDialog} onClose={() => setViewDialog(false)}>
        <DialogTitle sx={{ color: '#111', fontWeight: 700 }}>{t('Настройки вида')}</DialogTitle>
        <DialogContent sx={{ minWidth: 380 }}>
          <FormGroup>
            {ALL_COLUMNS.map(col => (
              <FormControlLabel
                key={col.field}
                control={
                  <Checkbox
                    checked={visibleColumns.includes(col.field)}
                    onChange={e => {
                      if (e.target.checked) {
                        setVisibleColumns(cols => [...cols, col.field]);
                      } else {
                        setVisibleColumns(cols => cols.filter(f => f !== col.field));
                      }
                    }}
                    sx={{ color: '#111', '&.Mui-checked': { color: '#111' } }}
                  />
                }
                label={columnLabels[col.field] || (typeof col.headerName === 'string' ? t(col.headerName) : col.headerName)}
                sx={{ color: '#111', fontWeight: 600 }}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(false)} variant="contained" sx={{ bgcolor: '#111', color: '#fff', '&:hover': { bgcolor: '#222' }, fontWeight: 600 }}>{t('Закрыть')}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={noteDialog.open} onClose={() => setNoteDialog({ open: false, orderId: null, value: '' })}>
        <DialogTitle>{t('Редактировать примечание')}</DialogTitle>
        <DialogContent>
          <NoteDialogInput
            value={noteDialog.value}
            onChange={val => setNoteDialog(d => ({ ...d, value: val }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialog({ open: false, orderId: null, value: '' })}>{t('Отмена')}</Button>
          <Button
            variant="contained"
            onClick={() => {
              onInternalNoteChange(noteDialog.orderId, noteDialog.value);
              setNoteDialog({ open: false, orderId: null, value: '' });
            }}
          >
            {t('Сохранить')}
          </Button>
        </DialogActions>
      </Dialog>
      <PendingEditDialog
        open={pendingEditDialog.open}
        orderId={pendingEditDialog.orderId}
        initialValues={pendingEditDialog.initialValues}
        editableServices={editableServices}
        onClose={handleClosePendingEditDialog}
        onSubmit={onPendingOrderEditSave}
      />
      <Dialog open={infoDialog.open} onClose={() => setInfoDialog({ open: false, row: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#1976d2', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoOutlinedIcon fontSize="medium" sx={{ color: '#1976d2' }} /> {t('Информация о заказе')}
        </DialogTitle>
        <DialogContent dividers>
          <InfoTable data={infoDialog.row} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialog({ open: false, row: null })} variant="contained" sx={{ bgcolor: '#111', color: '#fff', '&:hover': { bgcolor: '#222' }, fontWeight: 600 }}>{t('Закрыть')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 